import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  Glyph,
  Heading,
  Pill,
} from '@edreamsodigeo/prisma-design-system';
import {
  createFeature,
  createPersistedState,
  Show,
  Trans,
  useLogger,
  useTranslation,
  withErrorBoundary,
} from '@edreamsodigeo/frontend-core';

import MapCard from '~shared/MapCard/MapCard';
import useDataMeshTracking from './hooks/useDatameshTracking';
import useGoogleAnalyticsTracking from './hooks/useGoogleAnalyticsTracking';
import { useLogin } from '~clientState/providers/LoginProvider';
import getAccommodationInfoFromFlight from './utils/getAccommodationFromFlight';
import useContainerWidthListener from './hooks/useContainerWidthListener';
import getUserPermissions from './utils/getUserPermissions';
import { BuyPath } from '~types';
import { HSA_FLIGHT_SUMMARY_SESSION_STORAGE_KEY } from '~constants/storage';
import type { CheckoutSegment } from '~touchpoints/HotelDealsMobileCard/types';
import withModuleContext from '~hocs/withModuleContext';
import { withHotelsEnabled } from '~hocs/withHotelsEnabled';
import { defaultPassengers } from '~constants/defaultValues';
import useCampaignTranslate from '~touchpoints/AccommodationDeals/hooks/useCampaignTranslate/useCampaignTranslate';
import { useCampaignSuffix } from '~clientState/hooks';
import type { CampaignSuffix } from '~clientState/hooks/useCampaignSuffix/useCampaignSuffix';
import { XSELL_MKT_CAMPAIGN_MOBILE } from '~clientState/ab';
import getHeaderBackgroundStyle from './utils/getHeaderBackgroundStyle';
import getHeaderTextColor from './utils/getHeaderTextColor';
import getContainerStyle from './utils/getContainerStyle';
import getAmountOfNights from './utils/getAmountOfNights';

export interface FlightSummaryHotelsMapProps {
  segments: CheckoutSegment[];
  isMultiDestination?: boolean;
  isRoundTrip?: boolean;
  amountNights?: number;
  destinationName?: string;
  totalNumPassengers?: number;
  isMeta?: boolean;
}

const useHsaFlightSummaryStorage = createPersistedState({
  key: HSA_FLIGHT_SUMMARY_SESSION_STORAGE_KEY,
  type: 'sessionStorage',
  isGlobal: true,
});

const FlightSummaryHotelsMap: React.FC<FlightSummaryHotelsMapProps> = ({
  amountNights: amountNightsProp = 0,
  destinationName,
  segments,
  isMultiDestination = false,
  isRoundTrip = false,
  totalNumPassengers = defaultPassengers,
  isMeta = false,
}) => {
  const { isPrime, isLogged, seedOverride } = useLogin();
  const { containerRef, width } = useContainerWidthListener();
  const [_, setHsaFlightSummaryData] = useHsaFlightSummaryStorage();
  const { warn } = useLogger();
  // Datamesh tracking
  const { useDatameshTrackClick: trackDMTrackClick, useDatameshTrackLoad } =
    useDataMeshTracking();
  const datameshTrackLoadRef = useDatameshTrackLoad();

  // Google Analytics tracking
  const { trackClick: trackGAMapClick, trackOnLoad } =
    useGoogleAnalyticsTracking();
  useEffect(() => {
    trackOnLoad();
  }, [trackOnLoad]);

  const isOneWay = !isRoundTrip && !isMultiDestination;

  const amountNights = getAmountOfNights({
    segments,
    isOneWay,
    isMeta,
    amountNightsProp,
  });

  const userPermission = getUserPermissions(isPrime, isLogged);

  const { t } = useTranslation();

  const { checkInDate, checkOutDate, geoNodeId } =
    getAccommodationInfoFromFlight({
      segments,
      isRoundTrip,
      isMultiDestination,
    });

  if (!checkInDate || !checkOutDate || !geoNodeId) {
    warn(
      `Missing accommodation information: checkInDate=${checkInDate}, checkOutDate=${checkOutDate}, geoNodeId=${geoNodeId}`
    );
    return null;
  }

  destinationName = destinationName ?? segments[0]?.destination?.cityName;

  const saveFlightDataInStorage = () => {
    const arrivalDate: Date | undefined = isOneWay
      ? undefined
      : new Date(segments[1].arrivalDate);

    const originCity = segments[0]?.departure?.cityName;
    const departureDate = new Date(segments[0]?.departureDate);

    const hsaFlightSummaryData = {
      flightSummaryURL: window.location.href,
      originCity,
      destinationCity: destinationName,
      departureDate,
      ...(isRoundTrip && arrivalDate && { arrivalDate }),
      isMeta,
      totalNumPassengers,
    };

    setHsaFlightSummaryData(hsaFlightSummaryData);
  };

  if (!destinationName) {
    warn(`Missing flight information: destinationName=${destinationName}`);
    return null;
  }

  return (
    <Box sx={getContainerStyle(isMeta)} ref={containerRef}>
      <Box p="4" position="relative" ref={datameshTrackLoadRef}>
        <Show when={amountNights > 0}>
          <Pill
            type="informative"
            rounded
            sx={{
              display: 'flex',
              gap: 'tiny',
              position: 'absolute',
              alignItems: 'center',
              top: 0,
              transform: 'translateY(-50%)',
            }}
          >
            <Glyph
              name="Location"
              size={{ default: 'small' }}
              category="Content"
            />
            <span>
              {t(
                `summary.flightsummary.mobile${isMeta ? '_meta' : ''}.ribbon${amountNights > 1 ? '.plural' : '.singular'}`,
                {
                  amountNights,
                  destinationName,
                }
              )}
            </span>
          </Pill>
        </Show>
        <Heading
          type="3"
          color="neutral.90"
          weight="bold"
          sx={{ maxWidth: '327px' }}
        >
          <Trans
            i18nKey={`summary.flightsummary.mobile${isMeta ? '_meta' : ''}.title.${userPermission}`}
            values={{
              accommodationSaving: t(
                'variables.prime.savings.accommodation.total',
                {
                  currency: t('variables.global.currency'),
                }
              ),
              highlight: t(
                `summary.flightsummary.mobile${isMeta ? '_meta' : ''}.title_highlight.${userPermission}`
              ),
            }}
            components={{
              highlight: (
                <Box
                  display="inline"
                  sx={{
                    fontWeight: 'bold',
                    background:
                      '-webkit-linear-gradient(314.19deg, #F9700C 0%, #DA3835 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                />
              ),
            }}
          />
        </Heading>
      </Box>
      <HighligtedHeaderFeatured isMeta={isMeta} />
      <MapCard
        trackClickRef={trackDMTrackClick()}
        trackClick={trackGAMapClick}
        onMapClick={saveFlightDataInStorage}
        mapWidth={width}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        geoId={geoNodeId}
        buyPath={BuyPath.FlightSummary}
        roomRequests={[{ numAdults: totalNumPassengers, childrenAges: [] }]}
        ctaLabel={t(`summary.flightsummary.mobile${isMeta ? '_meta' : ''}.cta`)}
        openInSameTab
        ctaPosition="middle"
        showOpenedMap={false}
        seedOverride={seedOverride}
      />
    </Box>
  );
};

const HighligtedHeaderCampaignActive = ({ isMeta }: { isMeta: boolean }) => {
  const { translateWithCampaign } = useCampaignTranslate();
  const campaignSufix: CampaignSuffix = useCampaignSuffix();

  const headerColor = getHeaderTextColor(campaignSufix);

  return (
    <Box
      sx={getHeaderBackgroundStyle(campaignSufix)}
      p="small"
      data-testid="header"
    >
      <Flex alignContent="center" gap="tiny">
        <Glyph
          name="LightningBolt"
          category="Deals"
          size={{ default: 'base' }}
          color={headerColor}
        />
        <Heading type="6" color={headerColor}>
          {translateWithCampaign(
            `summary.flightsummary.mobile${isMeta ? '_meta' : ''}.map.header`
          )}
        </Heading>
      </Flex>
    </Box>
  );
};

const HighligtedHeaderCampaignInactive = ({ isMeta }: { isMeta: boolean }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        background: 'linear-gradient(314.19deg, #F9700C 0%, #DA3835 100%)',
      }}
      p="small"
    >
      <Flex alignContent="center" gap="tiny">
        <Glyph
          name="LightningBolt"
          category="Deals"
          size={{ default: 'base' }}
          color="white"
        />
        <Heading type="6" color="white">
          {t(`summary.flightsummary.mobile${isMeta ? '_meta' : ''}.map.header`)}
        </Heading>
      </Flex>
    </Box>
  );
};

const HighligtedHeaderFeatured = createFeature({
  name: XSELL_MKT_CAMPAIGN_MOBILE,
  variations: [
    HighligtedHeaderCampaignInactive,
    HighligtedHeaderCampaignActive,
  ],
  version: 1,
  LoadingComponent: () => null,
});

export default withModuleContext(
  withErrorBoundary(
    withHotelsEnabled({
      Component: FlightSummaryHotelsMap,
      FallbackComponent: () => null,
      options: { onlyPrimeSites: true },
    }),
    {
      name: 'FlightSummaryHotelsMap',
    }
  )
);
